<script>
	import { postApi } from "../service/api";
	import { ErrorCodes, getErrorMessage } from "../define/define";
	import { apiServer, SERVER_LIST, tokenManager } from "../stores";
	import { onMount } from "svelte";

	let formUserId = null;
	let formPassword = null;
	let rememberMe = false;
	let isLoading = false;
	let loginProgress = "";
	let serverStatuses = {}; // 서버별 로그인 상태
	let showPassword = false;

	// 페이지 로드 시 저장된 아이디 불러오기
	onMount(() => {
		const savedUserId = localStorage.getItem("rememberedUserId");
		if (savedUserId) {
			formUserId.value = savedUserId;
			rememberMe = true;
		}
		// 서버 상태 초기화
		initializeServerStatuses();
	});

	// 서버 상태 초기화
	function initializeServerStatuses() {
		serverStatuses = {};
		SERVER_LIST.forEach((server) => {
			serverStatuses[server.value] = {
				status: "waiting", // waiting, connecting, success, failed
				message: "대기 중",
				label: server.label,
			};
		});
	}

	// 서버 상태 업데이트
	function updateServerStatus(serverUrl, status, message) {
		serverStatuses[serverUrl] = {
			...serverStatuses[serverUrl],
			status,
			message,
		};
		// 반응성을 위해 객체 재할당
		serverStatuses = { ...serverStatuses };
	}

	const resetValues = () => {
		if (!rememberMe) {
			formUserId.value = "";
		}
		formPassword.value = "";
		formUserId.focus();
	};

	const onKeyPress = (e) => {
		if (e.keyCode === 13) onLogin();
	};

	// 단일 서버 로그인 함수
	async function loginToServer(serverUrl, credentials) {
		const protocol = serverUrl.includes("localhost") ? "http" : "https";
		const url = `${protocol}://${serverUrl}/user/login`;

		// 연결 시작 상태 업데이트
		updateServerStatus(serverUrl, "connecting", "연결 중...");

		try {
			const response = await fetch(url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(credentials),
				credentials: "include",
			});

			const result = await response.json();

			if (result.resultCode === ErrorCodes.OK) {
				const token = result.data.accessToken.replace("Bearer ", "");
				updateServerStatus(serverUrl, "success", "로그인 성공");
				return { success: true, token, server: serverUrl };
			} else {
				const errorMsg = getErrorMessage(result.resultCode);
				updateServerStatus(serverUrl, "failed", `실패: ${errorMsg}`);
				return { success: false, error: errorMsg, server: serverUrl };
			}
		} catch (error) {
			updateServerStatus(serverUrl, "failed", "연결 실패");
			return { success: false, error: "연결 실패", server: serverUrl };
		}
	}

	async function onLogin() {
		if (isLoading) return;

		if (formUserId.value.length < 1) {
			alert("아이디를 입력 하세요.");
			formUserId.focus();
			return false;
		}

		if (formPassword.value.length < 1) {
			alert("비밀번호를 입력 하세요.");
			formPassword.focus();
			return false;
		}

		isLoading = true;
		loginProgress = "로그인 시작...";

		// 서버 상태 초기화
		initializeServerStatuses();

		// Remember Me 처리
		if (rememberMe) {
			localStorage.setItem("rememberedUserId", formUserId.value);
		} else {
			localStorage.removeItem("rememberedUserId");
		}

		const credentials = {
			email: formUserId.value,
			password: formPassword.value,
		};

		try {
			// 모든 서버에 동시 로그인 시도
			loginProgress = "모든 서버에 로그인 중...";
			const loginPromises = SERVER_LIST.map((server) => loginToServer(server.value, credentials));

			const results = await Promise.all(loginPromises);

			// 성공한 로그인 결과 수집
			const successfulLogins = results.filter((result) => result.success);
			const failedLogins = results.filter((result) => !result.success);

			if (successfulLogins.length === 0) {
				alert("모든 서버 로그인에 실패했습니다.");
				resetValues();
				return;
			}

			// 토큰 저장
			const tokens = {};
			successfulLogins.forEach((result) => {
				tokens[result.server] = result.token;
			});

			// 토큰 매니저에 저장
			tokenManager.saveAllTokens(tokens);

			// 첫 번째 성공한 서버를 기본 서버로 설정 (China Test 우선)
			const defaultServer =
				successfulLogins.find((s) => s.server === "dev-api-backstage.blaupunkt-solum.cn") ||
				successfulLogins.find((s) => s.server === "dev-api-backstage.sleepwave.ai") ||
				successfulLogins[0];
			sessionStorage.setItem("AccessToken", tokens[defaultServer.server]);
			localStorage.setItem("selectedServer", defaultServer.server);

			// 로그인 결과 표시
			const successCount = successfulLogins.length;
			const totalCount = SERVER_LIST.length;

			loginProgress = `로그인 완료! (${successCount}/${totalCount} 서버 성공)`;

			// 2초 후 페이지 이동
			setTimeout(() => {
				resetValues();
				self.location.href = "/";
			}, 2000);
		} catch (error) {
			alert("로그인 중 오류가 발생했습니다.");
			resetValues();
		} finally {
			if (!loginProgress.includes("완료")) {
				isLoading = false;
				loginProgress = "";
			}
		}
	}

	// 상태별 아이콘 반환
	function getStatusIcon(status) {
		switch (status) {
			case "waiting":
				return "⏳";
			case "connecting":
				return "🔄";
			case "success":
				return "✅";
			case "failed":
				return "❌";
			default:
				return "⏳";
		}
	}

	// 상태별 색상 반환
	function getStatusColor(status) {
		switch (status) {
			case "waiting":
				return "#6c757d";
			case "connecting":
				return "#0d6efd";
			case "success":
				return "#198754";
			case "failed":
				return "#dc3545";
			default:
				return "#6c757d";
		}
	}
</script>

<div class="container-xxl">
	<div class="authentication-wrapper authentication-basic container-p-y">
		<div class="authentication-inner">
			<div class="card">
				<div class="card-body">
					<div class="app-brand justify-content-center">
						<a href="index.html" class="app-brand-link gap-2">
							<img alt="" src="/assets/img/logo.png" width="250" />
						</a>
					</div>

					<div class="mb-3">
						<label for="UserId" class="form-label">관리자아이디</label>
						<input type="text" class="form-control" placeholder="관리자아이디" on:keydown={onKeyPress} bind:this={formUserId} />
					</div>
					<div class="mb-3 form-password-toggle">
						<div class="d-flex justify-content-between">
							<label class="form-label" for="password">Password</label>
						</div>
						<div class="input-group input-group-merge">
							<input on:keydown={onKeyPress} type="{showPassword ? 'text' : 'password'}" class="form-control" placeholder="패스워드" bind:this={formPassword} />
							<span class="input-group-text cursor-pointer" on:click={() => showPassword = !showPassword}>
								<i class="bx bx-{showPassword ? 'show':'hide'}" />
							</span>
						</div>
					</div>
					<div class="mb-3">
						<div class="form-check">
							<input class="form-check-input" type="checkbox" id="remember-me" bind:checked={rememberMe} />
							<label class="form-check-label" for="remember-me">Remember Me</label>
						</div>
					</div>
					<div class="mb-3">
						<button class="btn btn-primary d-grid w-100" type="button" on:click={onLogin} disabled={isLoading} style="height: 40px;">
							{#if isLoading}
								<div class="d-flex align-items-center justify-content-center">
									<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									<span>{loginProgress || "로그인 중..."}</span>
								</div>
							{:else}
								<span>Sign in</span>
							{/if}
						</button>
					</div>

					<!-- 로그인 현황 텍스트 표시 -->
					{#if isLoading || loginProgress.includes("완료")}
						<div class="text-center mb-3">
							<small class="text-muted d-block mb-2">서버별 로그인 현황</small>
							{#each Object.entries(serverStatuses) as [serverUrl, status]}
								<div class="text-start mb-1" style="font-size: 0.8rem;">
									<span style="color: {getStatusColor(status.status)};">
										{getStatusIcon(status.status)}
										{status.label}: {status.message}
									</span>
								</div>
							{/each}
						</div>
					{/if}
				</div>
			</div>
		</div>
	</div>
</div>
